import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { DataTable } from '../../components/tables/DataTable';
import { Pagination } from '../../components/tables/Pagination';
import { PAGINATION_DEFAULT_PAGE_SIZE } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { sampleStatuses, sampleTypes } from '../../constants/statusOptions';
import { getAllSamples } from '../../services/sampleService';
import type { SampleWithDetails } from '../../types/location';
import { samplesColumns } from './samplesColumns';

export const SamplesPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<SampleWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('SampleReference');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_DEFAULT_PAGE_SIZE);
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getAllSamples());
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const projectOptions = useMemo(() => {
    const names = new Set(data.map((s) => s.ProjectName));
    return [...names].sort();
  }, [data]);

  const handleSort = (field: string) => {
    if (sortBy === field) setSortDir((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    return data.filter((s) => {
      if (filterProject && s.ProjectName !== filterProject) return false;
      if (filterStatus && s.Status !== filterStatus) return false;
      if (filterType && s.SampleType !== filterType) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (s.SampleReference ?? '').toLowerCase().includes(q)
        || s.SampleId.toLowerCase().includes(q)
        || s.ProjectName.toLowerCase().includes(q)
        || s.LocationName.toLowerCase().includes(q)
        || s.SampleType.toLowerCase().includes(q);
    });
  }, [data, search, filterProject, filterStatus, filterType]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortBy];
      const bVal = (b as unknown as Record<string, unknown>)[sortBy];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortBy, sortDir]);

  const resetPage = () => setPage(0);

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
        <Typography variant="h4" sx={{
          fontWeight: 800, color: 'text.primary', letterSpacing: '-0.025em',
          fontSize: '1.75rem', lineHeight: 1.2,
        }}>
          Samples
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search samples..."
          value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
          }}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.8125rem' } }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontSize: '0.8125rem' }}>Project</InputLabel>
          <Select value={filterProject} label="Project"
            onChange={(e) => { setFilterProject(e.target.value); resetPage(); }}
            sx={{ borderRadius: '10px', fontSize: '0.8125rem' }}>
            <MenuItem value="">All Projects</MenuItem>
            {projectOptions.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: '0.8125rem' }}>Status</InputLabel>
          <Select value={filterStatus} label="Status"
            onChange={(e) => { setFilterStatus(e.target.value); resetPage(); }}
            sx={{ borderRadius: '10px', fontSize: '0.8125rem' }}>
            <MenuItem value="">All Statuses</MenuItem>
            {sampleStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: '0.8125rem' }}>Type</InputLabel>
          <Select value={filterType} label="Type"
            onChange={(e) => { setFilterType(e.target.value); resetPage(); }}
            sx={{ borderRadius: '10px', fontSize: '0.8125rem' }}>
            <MenuItem value="">All Types</MenuItem>
            {sampleTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filtered.length} sample{filtered.length !== 1 ? 's' : ''} across{' '}
        {new Set(filtered.map((s) => s.ProjectKey)).size} project(s)
      </Typography>

      <DataTable
        columns={samplesColumns}
        data={sorted.slice(page * pageSize, (page + 1) * pageSize) as unknown as Record<string, unknown>[]}
        loading={loading}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        onRowClick={(row) => {
          const pid = row.ProjectId as string;
          const locKey = row.LocationKey as string;
          const sampleKey = row.SampleKey as string;
          if (pid) {
            const params = new URLSearchParams();
            if (locKey) params.set('location', locKey);
            if (sampleKey) params.set('sample', sampleKey);
            navigate(ROUTES.LOCATIONS.replace(':projectId', pid) + '?' + params.toString());
          }
        }}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); resetPage(); }}
        entityLabel="samples"
      />
    </Box>
  );
};

export default SamplesPage;
